import cn from 'classnames/bind';
import React, { FC, useState, useRef, useEffect } from 'react';

import {
    FALLBACK_AGE,
    VISIBLE_TIME,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/AgeRestriction/AgeRestriction.config';
import { useContainSize } from '@/scenarios/legacy-player/components/feature-player/hooks/skin/use-contain-size';
import { PlayingState } from '@/scenarios/legacy-player/types/instance';

import styles from './AgeRestriction.module.css';

const cx = cn.bind(styles);

type Props = {
    age: string | undefined;
    smokingWarningText?: string;
    videoWidth?: number;
    videoHeight?: number;
    playingState: PlayingState;
};

export const AgeRestriction: FC<Props> = ({
    age,
    playingState,
    smokingWarningText,
    videoWidth,
    videoHeight,
}) => {
    const isPositioned = videoHeight && videoHeight > 0 && videoWidth && videoWidth > 0;
    const [isVisible, setIsVisible] = useState(false);
    const [wasVisible, setWasVisible] = useState(false);
    const ageRestrictionTimerRef = useRef(0);
    const isPaused = playingState === PlayingState.pause;
    const [style, rootRef] = useContainSize<HTMLDivElement>({
        width: videoWidth || 0,
        height: videoHeight || 0,
        recalcOnResize: isVisible,
    });

    useEffect(() => {
        if (!isPaused) {
            // trigger visibility
            setWasVisible(false);
        } else {
            // hide on pause
            clearTimeout(ageRestrictionTimerRef.current);
            setIsVisible(false);
        }
    }, [isPaused]);

    useEffect(() => {
        if (!wasVisible && isPositioned) {
            setIsVisible(true);
            setWasVisible(true); // reset visibility trigger
            ageRestrictionTimerRef.current = window.setTimeout(() => {
                setIsVisible(false);
            }, VISIBLE_TIME);
            // clear timeout is intentionally in another hooks
        }
    }, [wasVisible, isPositioned]);

    // clear on unmount
    useEffect(() => () => clearTimeout(ageRestrictionTimerRef.current), []);

    if (age === null) {
        return null;
    }

    return (
        <div ref={rootRef} className={cx('root')}>
            <div style={style} className={cx('wrapper')}>
                <div
                    className={cx('warningBlock', {
                        warningBlock_positioned: isPositioned,
                        warningBlock_visible: isVisible,
                    })}
                >
                    <div className={cx('snippet')} dir="ltr">
                        {age ?? FALLBACK_AGE}
                    </div>
                    {smokingWarningText && (
                        <div className={cx('ageWarning')}>{smokingWarningText}</div>
                    )}
                </div>
            </div>
        </div>
    );
};
